import ExpandMore from "@mui/icons-material/ExpandMore";
import Undo from "@mui/icons-material/Undo";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { StatusChip, type StatusChipColor } from "components/StatusChip";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useState } from "react";

import type { ScoutDataChangeActors } from "../hooks/types";

import {
  formatDataChangeFieldValue,
  formatDataChangeProposalTitle,
  formatDataChangeResponseStatus,
  getDisplayDiffFields,
  isComplexDataChangeProposal,
  normalizeDataChangeDisplayVerb,
  type ScoutDataChangeCardStatus,
  type ScoutDataChangeProposal,
} from "./scoutDataChangeProposal";
import {
  formatUndoFailureDisplay,
  ScoutDataChangeUndoError,
  type ScoutDataChangeUndoResult,
} from "./scoutDataChangeUndoFailure";
import {
  ScoutDataChangeUndoFailureAlert,
  ShowUndoErrorButton,
} from "./ScoutDataChangeUndoFailureAlert";

type Props = {
  proposal: ScoutDataChangeProposal;
  /** Resolved status from history or deferred local stack decisions. */
  responseStatus?: ScoutDataChangeCardStatus;
  /**
   * When true, hide per-card Accept/Reject (reserved for future stack-only
   * chrome). Leave false so CTAs appear.
   */
  hideActions?: boolean;
  /** Disable Accept/Reject while a flush / sibling action is in flight. */
  actionsDisabled?: boolean;
  /** Applied + undoable + in-window. Hidden when expired, undone, or read-only. */
  canUndo?: boolean;
  /** Parent-driven in-flight undo. */
  undoInProgress?: boolean;
  /** Parent-driven undo failure. */
  undoFailure?: ScoutDataChangeUndoResult;
  onUndo?: () => Promise<void>;
  /** After a stale undo failure, ask Scout to propose compensating changes. */
  onRetryStaleUndo?: () => Promise<void>;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
  /** Staff Observe history: who proposed, confirmed, declined, applied, or undid. */
  actors?: ScoutDataChangeActors;
};

function actorLabel(
  verb: string,
  actor: { id: string; email?: string | null } | null | undefined
): string | null {
  if (!actor) {
    return null;
  }
  const who = actor.email?.trim() || actor.id;
  if (!who) {
    return null;
  }
  return `${verb} by ${who}`;
}

function DataChangeActorsCaption({ actors }: { actors: Props["actors"] }) {
  if (!actors) {
    return null;
  }
  const parts = [
    actorLabel("Proposed", actors.proposedBy),
    actorLabel("Confirmed", actors.confirmedBy),
    actorLabel("Declined", actors.declinedBy),
    actorLabel("Applied", actors.appliedBy),
    actorLabel("Undone", actors.undoneBy),
  ].filter((part): part is string => part !== null);
  if (parts.length === 0) {
    return null;
  }
  return (
    <Typography variant="caption" color="text.secondary" data-testid="scout-data-change-actors">
      {parts.join(" · ")}
    </Typography>
  );
}

type SubmittingAction = "confirm" | "decline" | null;

function dataChangeStatusChipColor(status: ScoutDataChangeCardStatus): StatusChipColor {
  if (status === "confirmed" || status === "applied") {
    return "success";
  }
  if (status === "apply_failed" || status === "undo_failed") {
    return "error";
  }
  if (status === "undone") {
    return "warning";
  }
  return "default";
}

const SAMPLE_PREVIEW_COUNT = 3;

const DIFF_GRID_COLUMNS = "minmax(96px, 30%) minmax(0, 1fr) minmax(0, 1fr)";

function DiffValueCell({
  value,
  emphasize = false,
  muted = false,
  placeholder = false,
}: {
  value?: unknown;
  emphasize?: boolean;
  muted?: boolean;
  placeholder?: boolean;
}) {
  const text = value === undefined ? "—" : formatDataChangeFieldValue(value);
  return (
    <Typography
      variant="body2"
      color={muted || placeholder || value === undefined ? "text.secondary" : "text.primary"}
      sx={{
        fontWeight: emphasize ? 600 : 400,
        wordBreak: "break-word",
        textDecoration: muted ? "line-through" : "none",
        fontStyle: placeholder && value === undefined ? "italic" : "normal",
      }}
      title={text}
    >
      {text}
    </Typography>
  );
}

function DiffFieldRow({
  label,
  before,
  after,
  isCreate,
}: {
  label: string;
  before?: unknown;
  after?: unknown;
  isCreate: boolean;
}) {
  const hasBefore = before !== undefined;
  const valuesDiffer =
    hasBefore &&
    after !== undefined &&
    formatDataChangeFieldValue(before) !== formatDataChangeFieldValue(after);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: DIFF_GRID_COLUMNS,
        columnGap: 1.5,
        alignItems: "start",
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, pt: 0.15, wordBreak: "break-word" }}
      >
        {label}
      </Typography>
      <DiffValueCell
        value={isCreate ? undefined : before}
        muted={valuesDiffer}
        placeholder={isCreate || !hasBefore}
      />
      <DiffValueCell value={after} emphasize={valuesDiffer || isCreate} />
    </Box>
  );
}

function DiffColumnHeaders() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: DIFF_GRID_COLUMNS,
        columnGap: 1.5,
        minWidth: 0,
        pb: 0.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Field
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Current
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Proposed
      </Typography>
    </Box>
  );
}

function SampleList({
  samples,
  caption,
}: {
  samples: Array<{ id: string; label: string }>;
  caption?: string;
}) {
  const preview = samples.slice(0, SAMPLE_PREVIEW_COUNT);
  const remainder = samples.slice(SAMPLE_PREVIEW_COUNT);

  return (
    <Box>
      {caption ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {caption}
        </Typography>
      ) : null}
      {preview.length > 0 ? (
        <Typography variant="body2" sx={{ mt: caption ? 0.25 : 0 }}>
          {preview.map((sample) => sample.label).join(", ")}
          {remainder.length > 0 ? "…" : ""}
        </Typography>
      ) : null}
      {remainder.length > 0 ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: "transparent",
            "&::before": { display: "none" },
            mt: 0.5,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore fontSize="small" />}
            sx={{ minHeight: 32, px: 0, "& .MuiAccordionSummary-content": { my: 0.5 } }}
          >
            <Typography variant="caption" color="primary">
              View all ({samples.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Stack spacing={0.5}>
              {samples.map((sample) => (
                <Typography key={sample.id} variant="body2" color="text.secondary">
                  {sample.label}
                </Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  );
}

function AffectedEntitiesBlock({ proposal }: { proposal: ScoutDataChangeProposal }) {
  const items = proposal.items;
  if (!items || items.length === 0) return null;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Affected ({items.length})
      </Typography>
      <SampleList samples={items} />
    </Box>
  );
}

function DataChangeUndoButton({
  isUndoing,
  disabled,
  onUndo,
}: {
  isUndoing: boolean;
  disabled: boolean;
  onUndo: () => void;
}) {
  return (
    <Button
      size="small"
      disabled={disabled || isUndoing}
      aria-busy={isUndoing}
      onClick={(event) => {
        event.stopPropagation();
        onUndo();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      startIcon={
        isUndoing ? <CircularProgress size={14} color="inherit" /> : <Undo fontSize="small" />
      }
    >
      {isUndoing ? "Undoing..." : "Undo Change"}
    </Button>
  );
}

function ImpactBlock({ proposal }: { proposal: ScoutDataChangeProposal }) {
  const impact = proposal.impact;
  if (!impact) return null;

  return (
    <Box
      sx={(theme) => ({
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        border: 1,
        borderColor: alpha(theme.palette.warning.main, 0.24),
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {impact.headline}
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {impact.related.map((related) => (
          <SampleList
            key={`${related.entityType}-${related.count}`}
            samples={related.samples ?? []}
            caption={`${related.entityType}: ${related.count}`}
          />
        ))}
      </Stack>
    </Box>
  );
}

function ProposalBody({
  proposal,
  showStaffDetails,
  readOnly = false,
}: {
  proposal: ScoutDataChangeProposal;
  showStaffDetails: boolean;
  readOnly?: boolean;
}) {
  const isComplex = isComplexDataChangeProposal(proposal);
  // Diff rows come from the staged proposal (agent/server). Do not fetch or
  // invent "current" values on the client — that duplicates Scout's propose path.
  const displayFields = getDisplayDiffFields(proposal);
  const hasFields = displayFields.length > 0;
  const isCreate = normalizeDataChangeDisplayVerb(proposal.diff.verb) === "Create";

  let emptyCopy = "Review this proposed change, then Accept or Reject.";
  if (readOnly) {
    emptyCopy = "No field details were recorded for this change.";
  }

  return (
    <Stack spacing={1.5}>
      {hasFields ? (
        <Stack spacing={1}>
          <DiffColumnHeaders />
          {displayFields.map((field) => (
            <DiffFieldRow
              key={field.label}
              label={field.label}
              before={field.before}
              after={field.after}
              isCreate={isCreate}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyCopy}
        </Typography>
      )}
      <AffectedEntitiesBlock proposal={proposal} />
      {isComplex ? <ImpactBlock proposal={proposal} /> : null}
      {showStaffDetails ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: "transparent",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore fontSize="small" />}
            sx={{ minHeight: 36, px: 0, "& .MuiAccordionSummary-content": { my: 0.5 } }}
          >
            <Typography variant="caption" color="text.secondary">
              GraphQL details (staff)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Typography
              component="pre"
              variant="caption"
              color="text.secondary"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              }}
            >
              {proposal.operation.graphql}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}

/**
 * One staged change card: semantic title, field diffs, optional impact,
 * Accept/Reject. After decision, collapses to an expandable read-only summary.
 */
export function ScoutDataChangeProposalCard({
  proposal,
  responseStatus,
  hideActions = false,
  actionsDisabled = false,
  canUndo = false,
  undoInProgress = false,
  undoFailure,
  onUndo,
  onRetryStaleUndo,
  onAccept,
  onReject,
  actors,
}: Props) {
  const { isStaff } = useCurrentUser();
  const [hasError, setHasError] = useState(false);
  const [undoError, setUndoError] = useState<unknown>(null);
  const [submittingAction, setSubmittingAction] = useState<SubmittingAction>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoCompleted, setUndoCompleted] = useState(false);
  const [undoErrorDismissed, setUndoErrorDismissed] = useState(false);

  const isSubmitting = submittingAction !== null;
  const isLockedOut = isSubmitting || actionsDisabled;
  const title = formatDataChangeProposalTitle(proposal);

  const handleConfirm = async () => {
    if (isSubmitting || actionsDisabled) return;
    setHasError(false);
    setSubmittingAction("confirm");
    try {
      await onAccept();
      setSubmittingAction(null);
    } catch {
      setHasError(true);
      setSubmittingAction(null);
    }
  };

  const handleDecline = async () => {
    if (isSubmitting || actionsDisabled) return;
    setHasError(false);
    setSubmittingAction("decline");
    try {
      await onReject();
      setSubmittingAction(null);
    } catch {
      setHasError(true);
      setSubmittingAction(null);
    }
  };

  const handleUndo = async () => {
    if (!onUndo || undoing || actionsDisabled) return;
    setHasError(false);
    setUndoError(null);
    setUndoErrorDismissed(false);
    setUndoing(true);
    try {
      await onUndo();
      setUndoCompleted(true);
    } catch (error) {
      setUndoError(error);
      setUndoCompleted(false);
    } finally {
      setUndoing(false);
    }
  };

  if (responseStatus) {
    const statusLabel = formatDataChangeResponseStatus(responseStatus);
    const isUndoing = !undoCompleted && (undoing || undoInProgress);
    const showUndo =
      Boolean(onUndo) && responseStatus === "applied" && !undoCompleted && (canUndo || isUndoing);
    let undoFailureSource: unknown = undoError;
    if (undoFailureSource == null && undoFailure) {
      undoFailureSource = new ScoutDataChangeUndoError(undoFailure);
    }
    const storedUndoError = formatUndoFailureDisplay(undoFailureSource);
    const displayedUndoError = undoErrorDismissed ? undefined : storedUndoError;
    return (
      <Box
        data-testid="scout-data-change-proposal-responded"
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.5,
            py: 1,
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded((current) => !current)}
          role="button"
          aria-expanded={isExpanded}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsExpanded((current) => !current);
            }
          }}
        >
          <StatusChip
            size="small"
            label={statusLabel}
            color={dataChangeStatusChipColor(responseStatus)}
            sx={{ flexShrink: 0 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          {showUndo ? (
            <DataChangeUndoButton
              isUndoing={isUndoing}
              disabled={actionsDisabled}
              onUndo={() => {
                void handleUndo();
              }}
            />
          ) : null}
          {storedUndoError && undoErrorDismissed ? (
            <ShowUndoErrorButton onShow={() => setUndoErrorDismissed(false)} />
          ) : null}
          <IconButton
            size="small"
            aria-label={isExpanded ? "Collapse change details" : "Expand change details"}
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: (theme) => theme.transitions.create("transform"),
            }}
          >
            <ExpandMore fontSize="small" />
          </IconButton>
        </Stack>
        {displayedUndoError ? (
          <ScoutDataChangeUndoFailureAlert
            display={displayedUndoError}
            onRetryStaleUndo={hideActions ? undefined : onRetryStaleUndo}
            retryDisabled={actionsDisabled}
            onDismiss={() => setUndoErrorDismissed(true)}
            sx={{ mx: 1.5, mb: 1 }}
          />
        ) : null}
        {actors ? (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <DataChangeActorsCaption actors={actors} />
          </Box>
        ) : null}
        <Collapse in={isExpanded} unmountOnExit>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
            <ProposalBody proposal={proposal} showStaffDetails={isStaff} readOnly />
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      data-testid="scout-data-change-proposal-card"
      data-complex={isComplexDataChangeProposal(proposal) ? "true" : "false"}
      sx={{
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <DataChangeActorsCaption actors={actors} />
        <ProposalBody proposal={proposal} showStaffDetails={isStaff} readOnly={hideActions} />
        {hasError ? (
          <Alert severity="error" onClose={() => setHasError(false)}>
            Something went wrong responding to this change. Please try again.
          </Alert>
        ) : null}
        {hideActions ? null : (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={handleDecline}
              disabled={isLockedOut}
              startIcon={
                submittingAction === "decline" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              onClick={handleConfirm}
              disabled={isLockedOut}
              startIcon={
                submittingAction === "confirm" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              Accept
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
